import { NavService } from '../../nav/service/nav.service';
import {
  AfterContentInit,
  Component,
  DestroyRef,
  inject,
  Inject,
  PLATFORM_ID,
  RESPONSE_INIT,
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { destinationAnimation, destinationImageAnimation } from '../destinationAnimations';
import { Meta, Title } from '@angular/platform-browser';
import { catchError, finalize, of } from 'rxjs';
import {
  CommonModule,
  isPlatformBrowser,
  isPlatformServer,
  NgOptimizedImage,
} from '@angular/common';
import { ArticleCardComponent } from '../../shared/components/article-card/article-card.component';
import { LazyLoadDirective } from '../../shared/directives/lazy-load.directive';
import { InnerHTMLComponent } from '../../shared/components/inner-html/inner-html.component';
import { ArticleCardLoadingComponent } from '../../shared/components/article-card-loading/article-card-loading.component';
import { DeviceService } from '../../shared/services/device.service';
import {
  DestinationService,
  ArticleService,
  Destination,
  Article,
} from '../../admin/shared/public-api';
import { MetaService } from '../../shared/services/meta.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-destination',
  templateUrl: './destination.component.html',
  styleUrls: ['./destination.component.scss'],
  animations: [destinationAnimation, destinationImageAnimation],
  imports: [
    CommonModule,
    RouterModule,
    ArticleCardComponent,
    ArticleCardLoadingComponent,
    LazyLoadDirective,
    NgOptimizedImage,
    InnerHTMLComponent,
  ],
})
export class DestinationComponent implements AfterContentInit {
  private platformId = inject(PLATFORM_ID);
  private destinationService = inject(DestinationService);
  private articleService = inject(ArticleService);
  private navService = inject(NavService);
  protected readonly deviceService = inject(DeviceService);
  private readonly metaTagService = inject(MetaService);
  private readonly titleService = inject(Title);

  country: string;
  destination: Destination;
  destinations: Destination[];
  hoverDestinations: { [id: string]: boolean } = {};
  articles: Article[];
  loading = true;
  endLazyLoading = false;
  private TAKE = 6;

  constructor(private route: ActivatedRoute) {
    this.country = this.route.snapshot.params['country'];
  }

  ngAfterContentInit(): void {
    this.navService.setTransparentBackground();
    this.loadDestinations(this.country);
    const $destination = this.destinationService.getDestinationByCountry(this.country);
    $destination.pipe(catchError(() => of(null))).subscribe((res) => {
      const destination = res;
      if (isPlatformBrowser(this.platformId) && !destination) {
        window.location.href = '/404';
      }
      //#region SEO
      this.titleService.setTitle(
        (this.route.snapshot.data['title'] as string).replace('$replace', destination!.country)
      );
      const baseUrl = environment.baseUrl;
      this.metaTagService.updateMetaTags(
        destination!.description,
        (this.route.snapshot.data['title'] as string).replace('$replace', destination!.country),
        `${baseUrl}${destination!.country}`,
        'website',
        `${baseUrl}${destination!.coverPicture.src}`,
        destination!.coverPicture.width,
        destination!.coverPicture.height,
        'image/jpeg'
      );
      //#endregion
      this.destination = destination!;
      this.loadArticles();
    });
  }

  loadArticles(): void {
    this.articles = [];
    this.getArticles();
  }

  getArticles(): void {
    this.loading = true;
    this.articleService
      .getDestinationArticles(this.destination.id, this.articles.length, this.TAKE)
      .pipe(
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((articles) => {
        this.endLazyLoading = articles.length < this.TAKE;
        this.articles.push(...articles);
      });
  }

  navigateToDestination(country: string): void {
    window.location.href = `/destination/${country}`;
  }

  loadDestinations(country: string): void {
    this.destinations = [];
    this.destinationService.getDestinations().subscribe((destinations) => {
      this.destinations = destinations.filter((x) => x.country !== country).slice(0, 3);
      this.destinations.forEach((d) => {
        this.hoverDestinations[d.id] = false;
      });
    });
  }

  hoverDestination(destinationId: string, state: boolean): void {
    if (this.hoverDestinations[destinationId] != null) {
      this.hoverDestinations[destinationId] = state;
    }
  }
}
